import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { API, errorText } from "@/lib/api";
import { trackConversion } from "@/lib/track";
import { PhoneVerify } from "@/components/PhoneVerify";

const Ctx = createContext({ request: () => {} });
export const useBrochure = () => useContext(Ctx);
const inputCls = "mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-[14px] outline-none transition-colors focus:border-[#0E9384] focus:ring-2 focus:ring-teal-100";
const saved = () => { try { return JSON.parse(localStorage.getItem("aor_contact") || "{}") || {}; } catch { return {}; } };

const Gate = ({ course, onClose }) => {
  const [f, setF] = useState({ name: "", email: "", consent_marketing: false });
  const [ph, setPh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!course) return;
    const s = saved();
    setF({ name: s.name || "", email: s.email || "", consent_marketing: false });
    setUrl(null);
  }, [course]);

  const submit = async (e) => {
    e.preventDefault();
    if (!ph) return toast.error("Please verify your mobile number first.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/brochure-request`, { course: course.key, ...f, phone: ph.phone, phone_token: ph.token });
      localStorage.setItem("aor_contact", JSON.stringify({ name: f.name, email: f.email }));
      setUrl(data?.download_url || null);
      trackConversion("curriculum", { transaction_id: `curriculum-${course.key}-${f.email}` });
      if (data?.download_url) window.location.href = data.download_url;
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={Boolean(course)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="brochure-gate" data-lenis-prevent className="max-h-[92vh] w-[calc(100vw-24px)] max-w-md overflow-y-auto rounded-2xl">
        {course && (url ? (
          <div data-testid="brochure-success" className="py-3 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#0E9384]" />
            <DialogTitle className="font-display mt-3 text-[22px] font-bold text-[#0B1B33]">Your download has started</DialogTitle>
            <DialogDescription className="mt-2 text-[15px] text-slate-700">We've also emailed the {course.title} curriculum to you.</DialogDescription>
            <a data-testid="brochure-download-again" href={url} className="btn-teal mx-auto mt-5 inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold"><Download className="h-4 w-4" /> Download again</a>
          </div>
        ) : (
          <form data-testid="brochure-form" onSubmit={submit} className="space-y-3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#0E9384]">Free curriculum PDF</p>
            <DialogTitle className="font-display text-[24px] font-bold leading-tight text-[#0B1B33]">Get the {course.title} curriculum</DialogTitle>
            <DialogDescription className="text-[14px] text-slate-600">Tell us where to send it. You'll get the download straight away, plus a copy by email.</DialogDescription>
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Full Name *</span><input data-testid="brochure-name-input" required maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></label>
            <label className="block"><span className="text-[13px] font-semibold text-slate-700">Email *</span><input data-testid="brochure-email-input" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></label>
            <PhoneVerify value={ph} onChange={setPh} testId="brochure-phone" />
            <label className="flex gap-2.5 text-[13px] text-slate-700">
              <Checkbox data-testid="brochure-consent-checkbox" checked={f.consent_marketing} onCheckedChange={(v) => setF({ ...f, consent_marketing: !!v })} className="mt-0.5 data-[state=checked]:border-[#0E9384] data-[state=checked]:bg-[#0E9384]" />
              Send me occasional updates about batches and live sessions. (Optional)
            </label>
            <button data-testid="brochure-submit-btn" disabled={busy} className="btn-teal flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-[16px] font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Download className="h-5 w-5" /> Download curriculum</>}
            </button>
            <p className="text-center text-[12.5px] text-slate-600">We respect your privacy. No spam.</p>
          </form>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export const BrochureProvider = ({ children }) => {
  const [course, setCourse] = useState(null);
  const request = useCallback((c) => setCourse(c), []);
  return (
    <Ctx.Provider value={{ request }}>
      {children}
      <Gate course={course} onClose={() => setCourse(null)} />
    </Ctx.Provider>
  );
};
